// src/components/tutorials/JS_Arrays.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button (opens in editorJS)
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

// 🔹 Random border color generator
function getRandomColor() {
  const colors = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#d97706",
    "#9333ea",
    "#0891b2",
    "#f59e0b",
    "#e11d48",
    "#0ea5e9",
    "#84cc16",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

// 🔹 Output Box Component
function OutputBox({ children }) {
  const borderColor = getRandomColor();
  return (
    <div
      className="output-box"
      style={{
        border: `2px dashed ${borderColor}`,
        transition: "border-color 0.3s ease",
      }}
    >
      {children}
    </div>
  );
}

export default function JS_Arrays() {
  const refs = {
    createArray: useRef(null),
    arrayMethods: useRef(null),
    loopingArrays: useRef(null),
    arraySearch: useRef(null),
    arrayConversion: useRef(null),
  };

  const scrollTo = (key) => {
    const r = refs[key];
    if (r && r.current) {
      r.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const keyPoints = [
    { key: "createArray", label: "Creating Arrays" },
    { key: "arrayMethods", label: "Array Methods" },
    { key: "loopingArrays", label: "Looping Through Arrays" },
    { key: "arraySearch", label: "Array Searching Methods" },
    { key: "arrayConversion", label: "Array Conversion" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Arrays</h1>

      <p className="tutorial-intro">
        Arrays in JavaScript are used to store multiple values in a single
        variable. They can hold items of any data type — numbers, strings,
        objects, or even other arrays.
      </p>

      {/* Key Points Section */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.4rem", margin: 0 }}>
          Array Concepts & Methods
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          Click a topic below to explore examples and test them interactively!
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1.25rem",
          }}
        >
          {keyPoints.map((l) => (
            <p key={l.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(l.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {l.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Creating Arrays */}
      <div ref={refs.createArray} className="tutorial-section">
        <h2 className="section-title">1️⃣ Creating Arrays</h2>
        <p>
          You can create arrays using array literals or the Array constructor.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
let numbers = new Array(10, 20, 30);
console.log(fruits);
console.log(numbers);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
let numbers = new Array(10, 20, 30);
console.log(fruits);
console.log(numbers);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>["Apple", "Banana", "Cherry"] [10, 20, 30]</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Array Methods */}
      <div ref={refs.arrayMethods} className="tutorial-section">
        <h2 className="section-title">2️⃣ Array Methods</h2>
        <p>
          Some useful array methods are{" "}
          <code className="highlight_code">push()</code>,{" "}
          <code className="highlight_code">pop()</code>,{" "}
          <code className="highlight_code">shift()</code>,{" "}
          <code className="highlight_code">unshift()</code>, and{" "}
          <code className="highlight_code">concat()</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana"];
fruits.push("Cherry");
console.log(fruits); // ["Apple", "Banana", "Cherry"]

fruits.pop();
console.log(fruits); // ["Apple", "Banana"]

let more = ["Mango", "Orange"];
let all = fruits.concat(more);
console.log(all);`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana"];
fruits.push("Cherry");
console.log(fruits);
fruits.pop();
console.log(fruits);
let more = ["Mango", "Orange"];
let all = fruits.concat(more);
console.log(all);`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>
            ["Apple", "Banana", "Cherry"] ["Apple", "Banana"] ["Apple",
            "Banana", "Mango", "Orange"]
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Looping Through Arrays */}
      <div ref={refs.loopingArrays} className="tutorial-section">
        <h2 className="section-title">3️⃣ Looping Through Arrays</h2>
        <p>
          You can use <code className="highlight_code">for</code>,{" "}
          <code className="highlight_code">for...of</code>, or{" "}
          <code className="highlight_code">forEach()</code> to iterate through
          array elements.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
fruits.forEach((item) => console.log(item));`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
fruits.forEach((item) => console.log(item));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>Apple Banana Cherry</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ Array Searching Methods */}
      <div ref={refs.arraySearch} className="tutorial-section">
        <h2 className="section-title">4️⃣ Array Searching Methods</h2>
        <p>
          Use <code className="highlight_code">indexOf()</code>,{" "}
          <code className="highlight_code">includes()</code>, and{" "}
          <code className="highlight_code">find()</code> to locate elements in
          arrays.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.indexOf("Banana")); // 1
console.log(fruits.includes("Mango")); // false
console.log(fruits.find(f => f.startsWith("C"))); // "Cherry"`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.indexOf("Banana"));
console.log(fruits.includes("Mango"));
console.log(fruits.find(f => f.startsWith("C")));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>1 false "Cherry"</pre>
        </OutputBox>
      </div>

      {/* 5️⃣ Array Conversion */}
      <div ref={refs.arrayConversion} className="tutorial-section">
        <h2 className="section-title">5️⃣ Array Conversion</h2>
        <p>
          You can convert arrays to strings or combine elements using{" "}
          <code className="highlight_code">toString()</code> and{" "}
          <code className="highlight_code">join()</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.toString()); // "Apple,Banana,Cherry"
console.log(fruits.join(" - ")); // "Apple - Banana - Cherry"`}
          />
          <TryItButton
            code={`let fruits = ["Apple", "Banana", "Cherry"];
console.log(fruits.toString());
console.log(fruits.join(" - "));`}
          />
        </div>

        <OutputBox>
          Output:
          <pre>"Apple,Banana,Cherry" "Apple - Banana - Cherry"</pre>
        </OutputBox>
      </div>

      {/* Footer */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Explore advanced methods like map(), filter(), and reduce().</li>
          <li>Learn about spread and rest operators.</li>
          <li>Understand shallow vs deep copying in arrays.</li>
        </ul>
      </div>
    </div>
  );
}
